import { useEffect, useState } from "react";
import {
  getReviews,
  submitReview,
  type PublicProduct,
  type Review,
} from "./api.ts";
import { formatMoney } from "./format.ts";
import { categoryLabel } from "./catalog.ts";
import { handleImgError } from "./img.ts";
import { haptic } from "./telegram.ts";
import { Stars } from "./Stars.tsx";

/** Composite cart key (mirrors Shop's keyOf; kept local to avoid a cycle). */
function keyOf(productId: string, variantId?: string | null): string {
  return variantId ? `${productId}::${variantId}` : productId;
}

/**
 * Product detail bottom-sheet. Tapping a catalog card opens this: a large
 * image, full description, category, stock state, add/stepper controls
 * (variant picker when the product has variants), favoriting, back-in-stock
 * alerts, and product reviews.
 */
export function ProductSheet({
  product,
  cart,
  isFavorite,
  onToggleFavorite,
  onSetQty,
  alerts,
  onToggleAlert,
  onClose,
}: {
  product: PublicProduct;
  cart: Record<string, number>;
  isFavorite: boolean;
  onToggleFavorite: (id: string) => void;
  onSetQty: (key: string, q: number) => void;
  alerts: Set<string>;
  onToggleAlert: (productId: string, variantId?: string | null) => void;
  onClose: () => void;
}) {
  // Lock body scroll while open; restore on close.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const hasVariants = product.variants.length > 0;
  // Selected variant: default to the first in-stock one.
  const [variantId, setVariantId] = useState<string | null>(
    hasVariants
      ? (product.variants.find((v) => v.inStock) ?? product.variants[0]!).id
      : null,
  );
  const variant = hasVariants
    ? product.variants.find((v) => v.id === variantId) ?? null
    : null;

  const key = keyOf(product.id, variant?.id);
  const qty = cart[key] ?? 0;
  const priceCents = variant ? variant.priceCents : product.priceCents;
  const availableStock = variant ? variant.availableStock : product.availableStock;
  const soldOut = variant ? !variant.inStock : !product.inStock;
  const low = !soldOut && availableStock <= 5;
  const alertOn = alerts.has(key);

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        className="sheet"
        role="dialog"
        aria-label={product.name}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-handle" onClick={onClose} />
        <div className="sheet-media">
          <img
            src={variant?.imageUrl ?? product.imageUrl}
            alt={variant ? `${product.name} — ${variant.name}` : product.name}
            onError={handleImgError}
          />
          {soldOut && <span className="stock-badge out">Sold out</span>}
          {low && (
            <span className="stock-badge low">
              Only {product.availableStock} left
            </span>
          )}
          <button
            className={isFavorite ? "fav-btn on" : "fav-btn"}
            aria-label={isFavorite ? "remove favorite" : "add favorite"}
            onClick={() => {
              haptic("light");
              onToggleFavorite(product.id);
            }}
          >
            {isFavorite ? "♥" : "♡"}
          </button>
        </div>

        <div className="sheet-body">
          <span className="sheet-category">{categoryLabel(product.category)}</span>
          <h2 className="sheet-name">{product.name}</h2>
          {product.badge && (
            <span className="product-badge sheet-badge">{product.badge}</span>
          )}
          {product.rating != null && (
            <Stars value={product.rating} count={product.ratingCount} />
          )}
          <div className="sheet-price">
            {formatMoney(priceCents, product.currency)}
          </div>
          <Specs product={product} />
          <p className="sheet-desc">{product.description}</p>

          {hasVariants && (
            <div className="sheet-variants">
              {product.variants.map((v) => (
                <button
                  key={v.id}
                  className={`chip ${v.id === variantId ? "chip-active" : ""} ${
                    v.inStock ? "" : "chip-out"
                  }`}
                  onClick={() => {
                    haptic("light");
                    setVariantId(v.id);
                  }}
                >
                  {v.imageUrl !== product.imageUrl && (
                    <img className="chip-thumb" src={v.imageUrl} alt="" onError={handleImgError} />
                  )}
                  {v.name}
                  {!v.inStock && " ⛔"}
                  {(cart[keyOf(product.id, v.id)] ?? 0) > 0 &&
                    ` ×${cart[keyOf(product.id, v.id)]}`}
                </button>
              ))}
            </div>
          )}

          {soldOut ? (
            <>
              <button className="btn" disabled>
                Out of stock
              </button>
              <button
                className={alertOn ? "btn secondary alert-on" : "btn secondary"}
                onClick={() => onToggleAlert(product.id, variant?.id ?? null)}
              >
                {alertOn ? "🔕 Stop notifying me" : "🔔 Notify me when it's back"}
              </button>
            </>
          ) : qty === 0 ? (
            <button
              className="btn"
              onClick={() => {
                onSetQty(key, 1);
              }}
            >
              Add to cart
            </button>
          ) : (
            <div className="sheet-stepper">
              <button
                className="step"
                aria-label="decrease"
                onClick={() => onSetQty(key, qty - 1)}
              >
                −
              </button>
              <span className="sheet-stepper-qty">{qty} in cart</span>
              <button
                className="step"
                aria-label="increase"
                disabled={qty >= availableStock}
                onClick={() => onSetQty(key, qty + 1)}
              >
                +
              </button>
            </div>
          )}
          <button
            className="btn secondary"
            onClick={() => {
              haptic("light");
              onClose();
            }}
          >
            Keep shopping
          </button>

          <Reviews productId={product.id} />
        </div>
      </div>
    </div>
  );
}

/** Product attribute chips (brand / flavor / nicotine strength), when present. */
function Specs({ product }: { product: PublicProduct }) {
  const specs: string[] = [];
  if (product.brand) specs.push(`🏷 ${product.brand}`);
  if (product.flavor) specs.push(`🍬 ${product.flavor}`);
  if (product.nicotineMg != null) specs.push(`💧 ${product.nicotineMg} mg`);
  if (specs.length === 0) return null;
  return (
    <div className="sheet-specs">
      {specs.map((s) => (
        <span className="sheet-spec" key={s}>
          {s}
        </span>
      ))}
    </div>
  );
}

/** Reviews list + a rating form for verified buyers. */
function Reviews({ productId }: { productId: string }) {
  const [reviews, setReviews] = useState<Review[] | null>(null);
  const [canReview, setCanReview] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const load = () => {
    getReviews(productId)
      .then((d) => {
        setReviews(d.reviews);
        setCanReview(d.canReview);
        const mine = d.reviews.find((r) => r.mine);
        if (mine) {
          setRating(mine.rating);
          setComment(mine.comment ?? "");
        }
      })
      .catch(() => setReviews([]));
  };
  useEffect(load, [productId]);

  const send = async () => {
    if (rating < 1) return;
    setBusy(true);
    try {
      await submitReview(productId, rating, comment.trim() || null);
      setDone(true);
      haptic("success");
      load();
    } catch {
      haptic("error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="reviews">
      <h3 className="reviews-title">Reviews</h3>

      {canReview && (
        <div className="review-form">
          <Stars value={rating || null} onRate={setRating} size={24} />
          <textarea
            className="field-input"
            rows={2}
            placeholder="Share your experience (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <button className="btn" disabled={busy || rating < 1} onClick={send}>
            {done ? "Updated ✓" : busy ? "Sending…" : "Submit review"}
          </button>
        </div>
      )}

      {reviews == null ? (
        <p className="hint">Loading reviews…</p>
      ) : reviews.length === 0 ? (
        <p className="hint">No reviews yet.</p>
      ) : (
        reviews.map((r) => (
          <div className="review" key={r.id}>
            <div className="review-head">
              <span className="review-author">{r.author}</span>
              <Stars value={r.rating} size={13} />
            </div>
            {r.comment && <p className="review-comment">{r.comment}</p>}
          </div>
        ))
      )}
    </div>
  );
}
